import { TEXT_PRIMARY } from '@/constants/common';
import { trans } from '@/i18n';
import { ExtractedImage, PreviewProps } from '@/types/plan';
import { processCssWithAttachments, processHtmlWithAttachments } from '@/utils/attachmentProcessor';
import { performSecurityCheck, sanitizeContent } from '@/utils/security';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { MdOutlineOpenInNew } from 'react-icons/md';

function PlanPreview({
  htmlFile,
  cssFile,
  jsFile,
  imageZipFile,
  fileUrls,
  images = [],
  width = '100%',
  height = '600px',
  allowInteraction = true,
}: PreviewProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extractedImages, setExtractedImages] = useState<ExtractedImage[]>([]);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const hasRequiredFiles = () => {
    if (fileUrls) {
      return fileUrls.htmlFile;
    }
    return htmlFile;
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;

        const fileType = getFileType(file.name);
        if (fileType) {
          const violations = performSecurityCheck(content, fileType);
          if (violations.length > 0) {
            const sanitizedContent = sanitizeContent(content, fileType);
            resolve(sanitizedContent);
          } else {
            resolve(content);
          }
        } else {
          resolve(content);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const getFileType = (fileName: string): 'html' | 'css' | 'js' | null => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'html':
      case 'htm':
        return 'html';
      case 'css':
        return 'css';
      case 'js':
        return 'js';
      default:
        return null;
    }
  };

  const getFileUrl = (fileType: string): string | null => {
    if (fileUrls && fileUrls[fileType]) {
      return fileUrls[fileType].url;
    }
    return null;
  };

  const loadCDNResource = async (url: string): Promise<string> => {
    try {
      const response = await fetch(url);
      return await response.text();
    } catch (error) {
      console.warn(`Failed to load CDN resource: ${url}`, error);
      return '';
    }
  };

  const createCompleteHTML = async (): Promise<string> => {
    let htmlContent = '';
    let cssContent = '';
    let jsContent = '';

    try {
      if (fileUrls) {
        const htmlUrl = getFileUrl('htmlFile');
        const cssUrl = getFileUrl('cssFile');
        const jsUrl = getFileUrl('jsFile');

        if (htmlUrl) {
          try {
            const response = await fetch(htmlUrl);
            htmlContent = await response.text();

            const violations = performSecurityCheck(htmlContent, 'html');
            if (violations.length > 0) {
              htmlContent = sanitizeContent(htmlContent, 'html');
            }

            if (images.length > 0) {
              htmlContent = await processHtmlWithAttachments(htmlContent, images);
            }
          } catch (error) {
            console.warn('Cannot read HTML file:', error);
            throw new Error('Cannot read HTML file');
          }
        }
        if (cssUrl) {
          try {
            const response = await fetch(cssUrl);
            cssContent = await response.text();

            const violations = performSecurityCheck(cssContent, 'css');
            if (violations.length > 0) {
              cssContent = sanitizeContent(cssContent, 'css');
            }

            if (images.length > 0) {
              cssContent = await processCssWithAttachments(cssContent, images);
            }
          } catch (error) {
            console.warn('Cannot read CSS file:', error);
          }
        }
        if (jsUrl) {
          try {
            const response = await fetch(jsUrl);
            jsContent = await response.text();

            const violations = performSecurityCheck(jsContent, 'js');
            if (violations.length > 0) {
              jsContent = sanitizeContent(jsContent, 'js');
            }
          } catch (error) {
            console.warn('Cannot read JS file:', error);
          }
        }
      } else {
        if (htmlFile) {
          try {
            htmlContent = await readFileAsText(htmlFile);

            if (images.length > 0) {
              htmlContent = await processHtmlWithAttachments(htmlContent, images);
            }
          } catch (error) {
            console.warn('Cannot read HTML file:', error);
            throw new Error('Cannot read HTML file');
          }
        }
        if (cssFile) {
          try {
            cssContent = await readFileAsText(cssFile);

            if (images.length > 0) {
              cssContent = await processCssWithAttachments(cssContent, images);
            }
          } catch (error) {
            console.warn('Cannot read CSS file:', error);
          }
        }
        if (jsFile) {
          try {
            jsContent = await readFileAsText(jsFile);
          } catch (error) {
            console.warn('Cannot read JS file:', error);
          }
        }
      }

      const [jqueryContent, wowContent, fontAwesomeContent, animateContent] = await Promise.all([
        loadCDNResource('https://cdnjs.cloudflare.com/ajax/libs/jquery/3.7.1/jquery.min.js'),
        loadCDNResource('https://cdnjs.cloudflare.com/ajax/libs/wow/1.1.2/wow.min.js'),
        loadCDNResource('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css'),
        loadCDNResource('https://cdnjs.cloudflare.com/ajax/libs/animate.css/3.5.2/animate.min.css'),
      ]);

      const completeHTML = `
          <!DOCTYPE html>
          <html lang="vi">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Preview</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }

              html, body {
                width: 100%;
                height: auto;
                margin: 0;
                padding: 0;
                scrollbar-width: none; /* Firefox */
                -ms-overflow-style: none; /* IE and Edge */
                overflow-x: hidden;
                overflow-y: auto;
              }

              html::-webkit-scrollbar,
              body::-webkit-scrollbar {
                display: none;
              }

              *::-webkit-scrollbar {
                display: none;
              }

              body {
                overflow-y: auto;
                overflow-x: hidden;
                min-height: 100vh;
              }

              img, video, iframe {
                max-width: 100%;
                height: auto;
              }

              h1, h2, h3, h4, h5, h6, p, ul, ol, li, div {
                margin: 0;
                padding: 0;
              }

              ${!allowInteraction
          ? `
                /* Disable interactions but allow scrolling */
                body {
                  pointer-events: none;
                }

                /* Allow scrolling on scrollable elements */
                body, html, [style*="overflow"], [style*="scroll"] {
                  pointer-events: auto !important;
                }

                /* Disable form interactions */
                input, textarea, select, button, a, [onclick], [onmousedown], [onmouseup] {
                  pointer-events: none !important;
                }

                /* Allow scrolling on specific elements */
                div, section, article, main, aside, nav {
                  pointer-events: none;
                }

                /* But allow scrolling on elements with overflow */
                div[style*="overflow"], section[style*="overflow"], article[style*="overflow"] {
                  pointer-events: auto !important;
                }
              `
          : ''
        }
            </style>

            ${fontAwesomeContent ? `<style>${fontAwesomeContent}</style>` : ''}
            ${animateContent ? `<style>${animateContent}</style>` : ''}
            ${cssContent ? `<style>${cssContent}</style>` : ''}
          </head>
          <body>
            ${htmlContent.replace('</html>', '').replace('</body>', '')}
            <script>${jqueryContent || ''}</script>
            <script>${wowContent || ''}</script>
            <script>${jsContent || ''}</script>
            <script>
              document.addEventListener("DOMContentLoaded", function () {
                new WOW().init();
              });
            </script>
          </body>
          </html>
        `;

      return completeHTML;
    } catch (error) {
      console.error('Error reading file:', error);
      throw error;
    }
  };

  const initializePreview = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!hasRequiredFiles()) {
        setError('HTML file is required to display preview');
        setIsLoading(false);
        return;
      }
      setExtractedImages([]);

      const completeHTML = await createCompleteHTML();

      const blob = new Blob([completeHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error initializing preview:', error);
      setError(error instanceof Error ? error.message : 'An error occurred while creating preview');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      extractedImages.forEach((img) => {
        URL.revokeObjectURL(img.url);
      });
    };
  }, [previewUrl, extractedImages]);

  useEffect(() => {
    initializePreview();
  }, [htmlFile, cssFile, jsFile, imageZipFile, fileUrls]);

  const handleOpenPreview = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  if (!hasRequiredFiles()) {
    return (
      <>
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontSize="lg" fontWeight="bold" color={TEXT_PRIMARY}>
            {trans('plan.preview')}
          </Text>
        </Flex>
        <Box
          width={width}
          height={height}
          borderColor="gray.300"
          borderRadius="md"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="gray.50"
        />
      </>
    );
  }

  if (isLoading) {
    return (
      <Box
        width={width}
        height={height}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="white"
      >
        <Flex direction="column" align="center" gap={3}>
          <Spinner size="lg" color="blue.500" />
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        width={width}
        height={height}
        border="1px solid"
        borderColor="red.200"
        borderRadius="md"
        bg="white"
        p={4}
      >
        <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" p={3} mb={4}>
          <Text color="red.600" fontWeight="bold">
            Error:
          </Text>
          <Text color="red.600">{error}</Text>
        </Box>
        <Button colorScheme="blue" onClick={initializePreview}>
          Try again
        </Button>
      </Box>
    );
  }

  return (
    <Box width={width}>
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontSize="lg" fontWeight="bold" color="gray.700">
          {trans('plan.preview')}
        </Text>
        <Flex gap={2}>
          <MdOutlineOpenInNew
            onClick={handleOpenPreview}
            size={20}
            style={{
              color: 'gray.400',
            }}
            cursor="pointer"
          />
        </Flex>
      </Flex>

      {/* Preview iframe */}
      <Box
        width="100%"
        height={height}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        overflow="hidden"
        bg="white"
      >
        {previewUrl ? (
          <iframe
            ref={iframeRef}
            src={previewUrl}
            width="100%"
            height="100%"
            style={{
              border: 'none',
              overflow: 'hidden',
            }}
            title="Preview"
            sandbox={
              allowInteraction
                ? 'allow-scripts allow-same-origin allow-forms allow-popups allow-modals'
                : 'allow-scripts allow-same-origin'
            }
          />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Text color="gray.500">Cannot load preview</Text>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default PlanPreview;
